// src/Pages/Hmo/PaymentSuccess.js
// ✅ COMPLETE: Payment success confirmation page with agent attribution & repay button
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function PaymentSuccess() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // ✅✅✅ READ FROM URL PARAMS FIRST (most reliable) ✅✅✅
  const reference = searchParams.get('reference');
  const amount = searchParams.get('amount');
  const period = searchParams.get('period');
  const applicantEmail = searchParams.get('email');
  const timestamp = searchParams.get('timestamp');
  const planOwnerId = searchParams.get('planOwnerId');
  
  // Context params for navigation
  const companyId = searchParams.get('company');
  const linkId = searchParams.get('link');
  const productId = searchParams.get('product');
  const agentId = searchParams.get('agent');  // ✅ Preserve agent attribution

  // ✅ Fallback to location.state only if URL params missing
  const paymentData = location.state || {};
  
  // ✅ Use URL params first, then state as fallback
  const finalReference = reference || paymentData.reference;
  const finalAmount = amount || paymentData.amount;
  const finalPeriod = period || paymentData.period;
  const finalEmail = applicantEmail || paymentData.applicantEmail;
  const finalTimestamp = timestamp || paymentData.timestamp;
  const finalPlanOwnerId = planOwnerId || paymentData.planOwnerId;

  // ✅ Brand Colors - ORANGE
  const PRIMARY_COLOR = '#F89423';
  const PRIMARY_LIGHT = '#FFF8F0';
  const PRIMARY_BORDER = '#FDE4C8';
  const PRIMARY_HOVER = '#E0851F';
  const SUCCESS_BG = '#F0FDF4';
  const SUCCESS_BORDER = '#22C55E';
  const SUCCESS_TEXT = '#166534';
  const SUCCESS_ICON = '#22C55E';

  // ✅ State for UI interactions
  const [emailSent, setEmailSent] = useState(false);
  const [loadingEmail, setLoadingEmail] = useState(false);

  // ✅ Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return new Date().toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    
    try {
      return new Date(dateString).toLocaleDateString('en-NG', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  // ✅ Format currency
  const formatCurrency = (value) => {
    if (!value) return '₦0';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return `₦${num.toLocaleString('en-NG')}`;
  };

  // ✅ Handle email receipt request
  const handleSendReceipt = async () => {
    if (!finalEmail) {
      toast.warning('No email address available to send receipt');
      return;
    }
    
    setLoadingEmail(true);
    
    try {
      // ✅ Simulate API call to send receipt
      // In production: call your backend endpoint
      // await authenticatedFetch(`${API_BASE}/Settings/send-receipt`, 'POST', {
      //   reference: finalReference,
      //   email: finalEmail,
      //   planOwnerId: finalPlanOwnerId
      // });
      
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setEmailSent(true);
      toast.success('Receipt sent to your email!');
      
    } catch (error) {
      console.error('💥 Error sending receipt:', error);
      toast.error('Failed to send receipt. Please try again.');
    } finally {
      setLoadingEmail(false);
    }
  };

  // ✅ Handle download receipt (PDF)
  const handleDownloadReceipt = () => {
    toast.info('Receipt download started...');
    
    // ✅ In production: Generate PDF or call backend endpoint
    // Example: window.open(`${API_BASE}/receipts/${finalReference}.pdf`, '_blank');
    
    // For demo: Show success message
    setTimeout(() => {
      toast.success('Receipt downloaded successfully!');
    }, 1000);
  };

  // ✅ Handle navigation to policy details
  const handleViewPolicy = () => {
    // ✅ Navigate to policy details with all context params
    const queryParams = new URLSearchParams();
    if (companyId) queryParams.set('company', companyId);
    if (linkId) queryParams.set('link', linkId);
    if (productId) queryParams.set('product', productId);
    if (agentId) queryParams.set('agent', agentId);  // ✅ Preserve agent
    if (finalPlanOwnerId) queryParams.set('planOwnerId', finalPlanOwnerId);
    
    const queryString = queryParams.toString();
    navigate(`/insurance/policy?${queryString}`);
  };

  // ✅ Handle navigation to dashboard
  const handleGoToDashboard = () => {
    navigate('/insurance-links', { replace: true });
  };

  // ✅✅✅ NEW: Handle "Start New Application" / Repay navigation ✅✅✅
  const handleRepay = () => {
    // ✅ Build query string with context params (exclude payment-specific params)
    const queryParams = new URLSearchParams();
    
    // ✅ Preserve context for navigation back to plan-owner
    if (companyId) queryParams.set('company', companyId);
    if (linkId) queryParams.set('link', linkId);
    if (productId) queryParams.set('product', productId);
    if (agentId) queryParams.set('agent', agentId);  // ✅ Critical: preserve agent attribution
    
    // ✅ Optional: Add a flag to indicate this is a new application
    queryParams.set('newApplication', 'true');
    
    const queryString = queryParams.toString();
    
    // ✅ Navigate back to plan-owner with context params
    // Use replace: true to prevent going back to payment success page
    navigate(`/insurance/plan-owner?${queryString}`, { replace: true });
    
    // ✅ Clear any cached applicant data to start fresh
    localStorage.removeItem('planOwnerDetails');
    
    // ✅ Show confirmation toast
    toast.info('Starting new application...');
  };

  const handleClose = () => {
    if (window.opener && !window.opener.closed) {
      window.close();
    } else {
      navigate('/', { replace: true });
    }
  };

  // ✅ Auto-redirect after 30 seconds (optional)
  useEffect(() => {
    const timer = setTimeout(() => {
      // Optional: Auto-redirect to dashboard after 30 seconds
      // handleGoToDashboard();
    }, 30000);
    
    return () => clearTimeout(timer);
  }, []);

  // ✅ Show warning ONLY if truly no data available
  useEffect(() => {
    // ✅ Check if we have at least reference OR planOwnerId
    if (!finalReference && !finalPlanOwnerId) {
      console.warn('⚠️ No payment details found:', {
        urlParams: { reference, amount, period, email: applicantEmail, planOwnerId },
        locationState: paymentData
      });
      
      toast.warning('No payment details found. Redirecting to dashboard...');
      
      // ✅ Redirect after 3 seconds
      setTimeout(() => {
        navigate('/insurance-links', { replace: true });
      }, 3000);
    }
  }, [finalReference, finalPlanOwnerId, navigate]);

  return (
    <div className="min-vh-100 d-flex flex-column" style={{ backgroundColor: '#f8f9fa' }}>
      <ToastContainer position="top-center" autoClose={3000} />
      
      {/* Header */}
      <header className="bg-white border-bottom py-3">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <img 
                src="/images/fsl_logo.png" 
                alt="FSL Logo" 
                style={{ height: '40px' }} 
              />
              <span className="ms-2 fw-bold" style={{ color: '#111827' }}>FSL HMO</span>
            </div>
            <button 
              className="btn btn-light rounded-circle d-flex align-items-center justify-content-center" 
              style={{ width: '36px', height: '36px' }} 
              onClick={handleClose}
              aria-label="Close"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow-1 py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-6 col-md-8">
              
              {/* ✅ Success Header */}
              <div className="text-center mb-4">
                <div 
                  className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                  style={{
                    backgroundColor: SUCCESS_BG,
                    color: SUCCESS_ICON,
                    width: '80px',
                    height: '80px',
                    fontSize: '32px',
                    border: `3px solid ${SUCCESS_BORDER}`
                  }}
                >
                  <i className="fas fa-check"></i>
                </div>
                <h1 className="fw-bold" style={{ fontSize: '28px', color: '#111827' }}>
                  Payment Successful!
                </h1>
                <p className="text-muted mb-0">
                  Your HMO plan has been activated
                </p>
                
                {/* ✅ Agent Attribution Badge */}
                {agentId && (
                  <div className="mt-3">
                    <span 
                      className="badge"
                      style={{ 
                        backgroundColor: `${PRIMARY_COLOR}15`,
                        color: PRIMARY_COLOR,
                        border: `1px solid ${PRIMARY_BORDER}`,
                        padding: '6px 12px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}
                    >
                      <i className="fas fa-user-tag me-1"></i>
                      Agent: {agentId.substring(0, 8)}...
                    </span>
                  </div>
                )}
              </div>

              {/* ✅ Payment Confirmation Card */}
              <div 
                className="rounded-3 p-4 mb-4 shadow-sm"
                style={{ backgroundColor: 'white', border: `1px solid ${SUCCESS_BORDER}` }}
              >
                {/* Success Banner */}
                <div 
                  className="rounded-3 p-3 mb-4 d-flex align-items-start gap-3"
                  style={{ backgroundColor: SUCCESS_BG, border: `1px solid ${SUCCESS_BORDER}` }}
                >
                  <i 
                    className="fas fa-check-circle mt-1"
                    style={{ color: SUCCESS_ICON, fontSize: '18px', flexShrink: 0 }}
                  ></i>
                  <div>
                    <p className="mb-1 fw-semibold" style={{ fontSize: '14px', color: SUCCESS_TEXT }}>
                      Payment Confirmed
                    </p>
                    <p className="mb-0" style={{ fontSize: '13px', color: SUCCESS_TEXT }}>
                      Transaction reference: <strong>{finalReference || 'N/A'}</strong>
                    </p>
                  </div>
                </div>

                {/* Payment Details Grid */}
                <div className="row mb-4">
                  <div className="col-6 mb-3">
                    <small className="text-muted d-block" style={{ fontSize: '12px' }}>
                      Amount Paid
                    </small>
                    <div className="fw-bold" style={{ fontSize: '18px', color: '#111827' }}>
                      {formatCurrency(finalAmount)}
                    </div>
                  </div>
                  <div className="col-6 mb-3">
                    <small className="text-muted d-block" style={{ fontSize: '12px' }}>
                      Coverage Period
                    </small>
                    <div className="fw-bold" style={{ fontSize: '16px', color: '#111827' }}>
                      {finalPeriod || 'N/A'}
                    </div>
                  </div>
                  <div className="col-6 mb-3">
                    <small className="text-muted d-block" style={{ fontSize: '12px' }}>
                      Transaction Date
                    </small>
                    <div className="fw-bold" style={{ fontSize: '14px', color: '#111827' }}>
                      {formatDate(finalTimestamp)}
                    </div>
                  </div>
                  <div className="col-6 mb-3">
                    <small className="text-muted d-block" style={{ fontSize: '12px' }}>
                      Plan Owner ID
                    </small>
                    <div className="fw-bold" style={{ fontSize: '14px', color: '#111827' }}>
                      #{finalPlanOwnerId || 'N/A'}
                    </div>
                  </div>
                </div>

                {/* Applicant Info */}
                {finalEmail && (
                  <div 
                    className="rounded-3 p-3 mb-4"
                    style={{ backgroundColor: PRIMARY_LIGHT, border: `1px solid ${PRIMARY_BORDER}` }}
                  >
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <i className="fas fa-user" style={{ color: PRIMARY_COLOR }}></i>
                      <small className="fw-semibold" style={{ fontSize: '13px', color: '#111827' }}>
                        Applicant
                      </small>
                    </div>
                    <p className="mb-0" style={{ fontSize: '14px', color: '#111827' }}>
                      {finalEmail}
                    </p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="d-grid gap-2">
                  {/* Send Receipt Button */}
                  <button
                    onClick={handleSendReceipt}
                    disabled={loadingEmail || emailSent || !finalEmail}
                    className="btn py-2 fw-semibold rounded-3 d-flex align-items-center justify-content-center gap-2"
                    style={{
                      backgroundColor: emailSent ? '#6c757d' : PRIMARY_COLOR,
                      color: 'white',
                      border: 'none',
                      opacity: loadingEmail || emailSent || !finalEmail ? 0.7 : 1,
                      cursor: loadingEmail || emailSent || !finalEmail ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!loadingEmail && !emailSent && finalEmail) {
                        e.target.style.backgroundColor = PRIMARY_HOVER;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!loadingEmail && !emailSent && finalEmail) {
                        e.target.style.backgroundColor = PRIMARY_COLOR;
                      }
                    }}
                  >
                    {loadingEmail ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        Sending...
                      </>
                    ) : emailSent ? (
                      <>
                        <i className="fas fa-check"></i>
                        Receipt Sent!
                      </>
                    ) : (
                      <>
                        <i className="fas fa-envelope"></i>
                        Email Receipt to {finalEmail?.split('@')[0]}...
                      </>
                    )}
                  </button>

                  {/* Download Receipt Button */}
                  <button
                    onClick={handleDownloadReceipt}
                    className="btn py-2 fw-semibold rounded-3 d-flex align-items-center justify-content-center gap-2"
                    style={{
                      backgroundColor: 'white',
                      color: PRIMARY_COLOR,
                      border: `2px solid ${PRIMARY_COLOR}`,
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = PRIMARY_LIGHT;
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'white';
                    }}
                  >
                    <i className="fas fa-download"></i>
                    Download Receipt (PDF)
                  </button>
                </div>
              </div>

              {/* ✅ Next Steps Card */}
              <div className="rounded-3 p-4 mb-4 shadow-sm" style={{ backgroundColor: 'white' }}>
                <h5 className="fw-semibold mb-3" style={{ color: '#111827', fontSize: '18px' }}>
                  <i className="fas fa-list-check me-2" style={{ color: PRIMARY_COLOR }}></i>
                  Next Steps
                </h5>
                
                <div className="d-flex flex-column gap-3">
                  {/* Step 1 */}
                  <div className="d-flex align-items-start gap-3">
                    <div 
                      className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{
                        backgroundColor: PRIMARY_LIGHT,
                        color: PRIMARY_COLOR,
                        width: '28px',
                        height: '28px',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}
                    >
                      1
                    </div>
                    <div>
                      <p className="mb-1 fw-semibold" style={{ fontSize: '14px' }}>
                        Check your email
                      </p>
                      <p className="mb-0 text-muted" style={{ fontSize: '13px' }}>
                        Your policy document and welcome kit will be sent to {finalEmail || 'your email'}
                      </p>
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="d-flex align-items-start gap-3">
                    <div 
                      className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{
                        backgroundColor: PRIMARY_LIGHT,
                        color: PRIMARY_COLOR,
                        width: '28px',
                        height: '28px',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}
                    >
                      2
                    </div>
                    <div>
                      <p className="mb-1 fw-semibold" style={{ fontSize: '14px' }}>
                        Download your HMO card
                      </p>
                      <p className="mb-0 text-muted" style={{ fontSize: '13px' }}>
                        Access your digital HMO card from your dashboard
                      </p>
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div className="d-flex align-items-start gap-3">
                    <div 
                      className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{
                        backgroundColor: PRIMARY_LIGHT,
                        color: PRIMARY_COLOR,
                        width: '28px',
                        height: '28px',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}
                    >
                      3
                    </div>
                    <div>
                      <p className="mb-1 fw-semibold" style={{ fontSize: '14px' }}>
                        Start using your coverage
                      </p>
                      <p className="mb-0 text-muted" style={{ fontSize: '13px' }}>
                        Visit any network hospital with your HMO card to access care
                      </p>
                    </div>
                  </div>
                </div>

                {/* View Policy Button */}
                <button
                  onClick={handleViewPolicy}
                  className="btn w-100 mt-4 py-3 fw-semibold rounded-3"
                  style={{
                    backgroundColor: PRIMARY_COLOR,
                    color: 'white',
                    border: 'none',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = PRIMARY_HOVER;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = PRIMARY_COLOR;
                  }}
                >
                  <i className="fas fa-file-medical me-2"></i>
                  View My Policy Details
                </button>
                
                {/* ✅✅✅ NEW: Repay / Start New Application Button ✅✅✅ */}
                <div className="mt-4 pt-4 border-top">
                  <button
                    onClick={handleRepay}
                    className="btn w-100 py-3 fw-semibold rounded-3"
                    style={{
                      backgroundColor: 'white',
                      color: '#6c757d',
                      border: `2px solid #6c757d`,
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#f8f9fa';
                      e.target.style.color = PRIMARY_COLOR;
                      e.target.style.borderColor = PRIMARY_COLOR;
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'white';
                      e.target.style.color = '#6c757d';
                      e.target.style.borderColor = '#6c757d';
                    }}
                  >
                    <i className="fas fa-redo me-2"></i>
                    Start New Application
                  </button>
                  <p className="text-muted small text-center mt-2 mb-0">
                    Begin a new application for yourself or another person
                  </p>
                </div>
              </div>

              {/* Support Card */}
              <div 
                className="rounded-3 p-4 text-center"
                style={{ backgroundColor: 'white' }}
              >
                <p className="text-muted mb-3" style={{ fontSize: '14px' }}>
                  Need help with your policy?
                </p>
                <div className="d-flex flex-column flex-sm-row gap-2 justify-content-center">
                  <a 
                    href="mailto:support@fsl.ng"
                    className="btn py-2 px-4 fw-semibold rounded-3"
                    style={{
                      backgroundColor: 'white',
                      color: PRIMARY_COLOR,
                      border: `2px solid ${PRIMARY_COLOR}`,
                      transition: 'all 0.3s ease',
                      fontSize: '14px'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = PRIMARY_LIGHT;
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'white';
                    }}
                  >
                    <i className="fas fa-envelope me-1"></i>
                    Email Support
                  </a>
                  <a 
                    href="tel:+234700FSLHELP"
                    className="btn py-2 px-4 fw-semibold rounded-3"
                    style={{
                      backgroundColor: PRIMARY_COLOR,
                      color: 'white',
                      border: 'none',
                      transition: 'all 0.3s ease',
                      fontSize: '14px'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = PRIMARY_HOVER;
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = PRIMARY_COLOR;
                    }}
                  >
                    <i className="fas fa-phone me-1"></i>
                    Call Support
                  </a>
                </div>
                <p className="text-muted small mt-3 mb-0">
                  Mon-Fri, 8am-6pm WAT
                </p>
              </div>

              {/* Back to Dashboard Link */}
              <div className="text-center mt-4">
                <button
                  onClick={handleGoToDashboard}
                  className="btn btn-link text-muted"
                  style={{ fontSize: '14px', textDecoration: 'none' }}
                >
                  <i className="fas fa-arrow-left me-1"></i>
                  Back to Dashboard
                </button>
              </div>

            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-top py-3 mt-auto">
        <div className="container text-center">
          <p className="text-muted small mb-0">
            <i className="fas fa-shield-alt me-1" style={{ color: PRIMARY_COLOR }}></i>
            Powered by <span style={{ fontWeight: '600', color: PRIMARY_COLOR }}>FSL</span>
          </p>
          <p className="text-muted small mb-0 mt-1">
            © {new Date().getFullYear()} FSL Management Services. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default PaymentSuccess;